import { useEffect, useState } from "react";
import { useNavigate, useParams, Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, Loader2, LayoutTemplate } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr, mergeBranding, mergeForm } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { VideoSourceField } from "@/components/VideoSourceField";
import { DesignPanel } from "@/components/DesignPanel";
import { FormBuilder } from "@/components/FormBuilder";
import { LandingPage } from "@/components/LandingPage";
import { SpeakersEditor } from "@/components/SpeakersEditor";
import { MediaEditor } from "@/components/MediaEditor";
import { ChatEditor } from "@/components/ChatEditor";
import { ReportEditor } from "@/components/ReportEditor";
import { AssetsEditor } from "@/components/AssetsEditor";
import { ImportHtmlDialog } from "@/components/ImportHtmlDialog";
import { ThankYouEditor } from "@/components/ThankYouEditor";
import { SponsorsEditor } from "@/components/SponsorsEditor";
import { ThankYouScreen } from "@/components/ThankYouScreen";

const EMPTY = {
  name: "", slug: "", title: "", headline: "", description: "", cta_text: "Watch the video", form_heading: "Complete the form below",
  status: "active", video_type: "url", video_url: "", video_path: "", video_filename: "",
  end_cta_text: "", end_cta_url: "", branding: mergeBranding(), form: mergeForm(),
  program_id: "", cta_action: "auto", campaign_type: "webinar", assets: [], recommend_after_seconds: 180, hub_message: "",
  speakers_heading: "Speakers", speakers: [], footer_text: "",
  hero_image_path: "", thumbnail_path: "", top_header: { text: "", color: "", size: "md" },
  image_styles: { hero: {}, thumbnail: {}, takeaways: {} },
  takeaways: { heading: "Key takeaways", text: "", image_path: "" },
  gallery: { heading: "Gallery", images: [] },
  partners: { heading: "Brought to you by", images: [] },
  chat: { enabled: false, name: "Event Assistant", avatar_path: "", color: "#EC4899", welcome: "Hi! Ask me anything about this session.", greeting: "Hi! Got questions about this webinar?", suggestions: [], knowledge: "" },
  sponsors: { label: "Sponsored by", size: "md", logos: [] },
  thank_you: { enabled: false, mode: "continue", heading: "Thank you!", message: "Your registration is confirmed.", button_text: "Continue", auto_seconds: 5, calendar_start: "", calendar_end: "", calendar_location: "", link_text: "", link_url: "" },
  report_template: { heading: "Attendance Report", subtitle: "Certificate of viewing", footer: "", logo_path: "", show_date: true, show_email: true, show_start: true, show_end: true, show_duration: true },
};

const withDefaults = (d) => ({
  ...EMPTY, ...d, branding: mergeBranding(d.branding), form: mergeForm(d.form),
  takeaways: { ...EMPTY.takeaways, ...(d.takeaways || {}) },
  top_header: { ...EMPTY.top_header, ...(d.top_header || {}) },
  image_styles: { ...EMPTY.image_styles, ...(d.image_styles || {}) },
  gallery: { ...EMPTY.gallery, ...(d.gallery || {}) },
  partners: { ...EMPTY.partners, ...(d.partners || {}) },
  chat: { ...EMPTY.chat, ...(d.chat || {}) },
  report_template: { ...EMPTY.report_template, ...(d.report_template || {}) },
  thank_you: { ...EMPTY.thank_you, ...(d.thank_you || {}) },
  sponsors: { ...EMPTY.sponsors, ...(d.sponsors || {}) },
});

const inputCls = "w-full h-11 px-4 rounded-md bg-[#0B0F17] border border-[#242C3D] focus:border-indigo-500 focus:outline-none text-sm transition-colors";

const Field = ({ label, hint, children }) => (
  <div>
    <label className="block font-mono text-[11px] uppercase tracking-widest text-slate-500 mb-2">{label}</label>
    {children}
    {hint && <p className="text-xs text-slate-500 mt-1.5">{hint}</p>}
  </div>
);

const Section = ({ title, sub, children, delay = "" }) => (
  <section className={`rounded-lg border border-[#242C3D] bg-[#141A24]/70 p-6 space-y-5 sv-rise ${delay}`}>
    <div>
      <h2 className="font-display font-semibold text-lg">{title}</h2>
      {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
    </div>
    {children}
  </section>
);

const ScaledPreview = ({ children }) => {
  const [ref, setRef] = useState(null);
  const [scale, setScale] = useState(0.35);
  useEffect(() => {
    if (!ref) return;
    const ro = new ResizeObserver(() => setScale(Math.min(ref.clientWidth / 1200, 1)));
    ro.observe(ref);
    return () => ro.disconnect();
  }, [ref]);
  return (
    <div ref={setRef} className="relative overflow-hidden rounded-lg border border-[#242C3D] bg-black" style={{ height: 780 * scale }}>
      <div style={{ width: 1200, height: 780, transform: `scale(${scale})`, transformOrigin: "top left" }} className="overflow-hidden">{children}</div>
    </div>
  );
};

export default function CampaignEditor() {
  const { user } = useAuth();
  const { id } = useParams();
  const nav = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [tyPreview, setTyPreview] = useState(false);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const applyImport = (draft, replaceForm) => setForm((f) => {
    const { branding = {}, form: dForm, ...rest } = draft;
    return withDefaults({
      ...f, ...rest, branding: { ...f.branding, ...branding },
      form: replaceForm && dForm ? { ...f.form, ...dForm } : f.form,
      name: f.name || rest.title || "",
    });
  });
  const [params] = useSearchParams();
  const [programs, setPrograms] = useState([]);
  const [hideThumb, setHideThumbState] = useState(false);
  const setHideThumb = async (v) => {
    setHideThumbState(v);
    try { await api.put("/settings", { hide_video_thumbnail: v }); toast.success(v ? "Video thumbnail hidden on all landing pages" : "Video thumbnail shown on landing pages"); }
    catch (ex) { setHideThumbState(!v); toast.error(formatErr(ex)); }
  };
  useEffect(() => {
    api.get("/programs").then((r) => setPrograms(r.data));
    api.get("/settings").then((r) => setHideThumbState(!!r.data.hide_video_thumbnail));
    if (!id && params.get("program")) set({ program_id: params.get("program") });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (id) api.get(`/campaigns/${id}`).then((r) => setForm(withDefaults(r.data)));
  }, [id]);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = id ? await api.put(`/campaigns/${id}`, form) : await api.post("/campaigns", form);
      toast.success(id ? "Landing page updated" : "Landing page created");
      nav(!id && form.program_id ? `/admin/programs/${form.program_id}` : `/admin/campaigns/${data.id}`);
    } catch (ex) {
      toast.error(formatErr(ex));
    } finally {
      setBusy(false);
    }
  };

  const previewCampaign = { ...form, title: form.title || "Your campaign title", hide_video_thumbnail: hideThumb };
  const slugPreview = form.slug.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

  return (
    <div className="grid xl:grid-cols-[minmax(0,1fr)_480px] gap-8 items-start">
      <div>
        <Link to="/admin" data-testid="editor-back-link" className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-white mb-6"><ArrowLeft className="w-4 h-4" /> Back</Link>
        <div className="flex flex-wrap items-center justify-between gap-4 mb-4 sv-rise">
          <h1 className="font-display text-4xl font-bold tracking-tight">{id ? "Edit landing page" : "New landing page"}</h1>
          <ImportHtmlDialog onApply={applyImport} />
        </div>
        {form.template_id && (
          <p data-testid="editor-template-linked" className="sv-rise text-xs mb-6 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-indigo-500/10 border border-indigo-500/40 text-indigo-300">
            <LayoutTemplate className="w-3.5 h-3.5" /> Linked to a template — saving updates it for everyone.
          </p>
        )}
        <form onSubmit={save} className="space-y-8" data-testid="campaign-form">
          <Section title="Details" delay="sv-d1">
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Internal name"><input data-testid="campaign-name-input" required value={form.name} onChange={(e) => set({ name: e.target.value })} className={inputCls} placeholder="Q3 Product Demo" /></Field>
              <Field label="Landing page URL" hint={slugPreview ? `${window.location.origin}/c/${slugPreview}` : "Leave blank to generate from the name. Must be unique."}>
                <div className="flex items-center rounded-md bg-[#0B0F17] border border-[#242C3D] focus-within:border-indigo-500">
                  <span className="pl-4 font-mono text-sm text-slate-500 select-none">/c/</span>
                  <input data-testid="campaign-slug-input" value={form.slug} onChange={(e) => set({ slug: e.target.value })} className="flex-1 h-11 pr-4 pl-1 bg-transparent font-mono text-sm focus:outline-none" placeholder="your-custom-name" />
                </div>
              </Field>
            </div>
            <Field label="Campaign" hint="Landing pages and their content are tagged to a campaign and listed in its content hub.">
              <select data-testid="campaign-program-select" value={form.program_id} onChange={(e) => set({ program_id: e.target.value })} className={inputCls}>
                <option value="">— Not in a campaign —</option>
                {programs.map((p) => <option key={p.id} value={p.id}>{`${p.code} — ${p.name}`}</option>)}
              </select>
            </Field>
            <Field label="Type">
              <div className="flex gap-2">
                {[["webinar", "Webinar / video"], ["whitepaper", "White paper syndication"]].map(([v, l]) => (
                  <button key={v} type="button" data-testid={`campaign-type-${v}`} onClick={() => set({ campaign_type: v, ...(v === "whitepaper" && form.cta_text === "Watch the video" ? { cta_text: "Read the white paper" } : {}) })}
                    className={`h-9 px-4 rounded-full text-sm border transition-colors ${form.campaign_type === v ? "border-indigo-500 bg-indigo-500/15 text-indigo-200" : "border-[#242C3D] text-slate-400 hover:text-white"}`}>{l}</button>
                ))}
              </div>
            </Field>
            <Field label="Status">
              <div className="flex gap-2">
                {["active", "draft"].map((s) => (
                  <button key={s} type="button" data-testid={`campaign-status-${s}`} onClick={() => set({ status: s })}
                    className={`h-9 px-4 rounded-full text-sm border transition-colors ${form.status === s ? "border-indigo-500 bg-indigo-500/15 text-indigo-200" : "border-[#242C3D] text-slate-400 hover:text-white"}`}>
                    {s === "active" ? "Active (public)" : "Draft (hidden)"}
                  </button>
                ))}
              </div>
            </Field>
          </Section>

          <Section title="Landing page content" delay="sv-d1">
            <Field label="Title"><input data-testid="campaign-title-input" required value={form.title} onChange={(e) => set({ title: e.target.value })} className={inputCls} placeholder="See how teams ship 3× faster" /></Field>
            <Field label="Headline"><input data-testid="campaign-headline-input" value={form.headline} onChange={(e) => set({ headline: e.target.value })} className={inputCls} placeholder="A 7-minute walkthrough of the new workflow" /></Field>
            <Field label="Description">
              <textarea data-testid="campaign-description-input" rows={4} value={form.description} onChange={(e) => set({ description: e.target.value })} className={`${inputCls} h-auto py-3 resize-none`} placeholder="What will viewers learn?" />
            </Field>
            <Field label="Form button (CTA)" hint={'"Download now" downloads the PDF straight after the form and opens the reader.'}>
              <div className="flex flex-wrap gap-2 mb-3">
                {[["read", "Read now"], ["download", "Download now"], ["watch", "Watch now"], ["access", "Access now"]].map(([a, l]) => (
                  <button key={a} type="button" data-testid={`campaign-cta-preset-${a}`} onClick={() => set({ cta_action: a, cta_text: l })}
                    className={`h-9 px-4 rounded-full text-sm border transition-colors ${form.cta_action === a && form.cta_text === l ? "border-indigo-500 bg-indigo-500/15 text-indigo-200" : "border-[#242C3D] text-slate-400 hover:text-white"}`}>{l}</button>
                ))}
              </div>
              <input data-testid="campaign-cta-input" value={form.cta_text} onChange={(e) => set({ cta_text: e.target.value })} className={inputCls} placeholder="Or type your own button text" />
            </Field>
            <Field label="Form panel heading" hint='Tip: lines in the description starting with "- " render as a bullet list'><input data-testid="campaign-form-heading-input" value={form.form_heading} onChange={(e) => set({ form_heading: e.target.value })} className={inputCls} placeholder="Complete the form below" /></Field>
          </Section>

          <Section title="Design" sub="Logo, colours, fonts and layout — the preview updates live." delay="sv-d2">
            <DesignPanel branding={form.branding} setBranding={(branding) => set({ branding })} />
          </Section>

          <Section title="Sponsor logos" sub="Up to 3 logos in the top-right corner. If your main logo is set to the right, it moves to the left." delay="sv-d2">
            <SponsorsEditor value={form.sponsors} onChange={(sponsors) => set({ sponsors })} />
          </Section>

          <Section title="Images & sections" sub="Header above banner, hero banner, video thumbnail, key takeaways, gallery and partner logos. Empty sections are hidden." delay="sv-d2">
            <MediaEditor form={form} set={set} hideThumb={hideThumb} setHideThumb={setHideThumb} canEditGlobal={user?.role === "admin"} />
          </Section>

          <Section title="AI chat agent" sub="A chat bubble on this landing page that answers visitors' questions." delay="sv-d2">
            <ChatEditor chat={form.chat} setChat={(chat) => set({ chat })} />
          </Section>

          <Section title="Speakers" sub="Shown in the left panel under the content. Add photos, names and titles." delay="sv-d2">
            <SpeakersEditor heading={form.speakers_heading} speakers={form.speakers} set={set} />
          </Section>

          <Section title="Form" sub="Email is always collected. Add qualifying questions and consent checkboxes." delay="sv-d2">
            <FormBuilder form={form.form} setForm={(f) => set({ form: f })} />
          </Section>

          {form.campaign_type === "whitepaper" ? (
            <Section title="White papers & content hub" sub="Readers enter their email on the landing page, then read in your branded content hub." delay="sv-d3">
              <AssetsEditor form={form} set={set} />
            </Section>
          ) : (
          <Section title="Video" delay="sv-d3">
            <VideoSourceField form={form} set={set} />
          </Section>
          )}

          <Section title="End screen button" sub="Shown when the video finishes. Leave the link empty to hide it." delay="sv-d3">
            <div className="grid sm:grid-cols-[1fr_1.5fr] gap-5">
              <Field label="Button text"><input data-testid="campaign-end-cta-text-input" value={form.end_cta_text} onChange={(e) => set({ end_cta_text: e.target.value })} className={inputCls} placeholder="Book a demo" /></Field>
              <Field label="Button link"><input data-testid="campaign-end-cta-url-input" type="url" value={form.end_cta_url} onChange={(e) => set({ end_cta_url: e.target.value })} className={inputCls} placeholder="https://calendly.com/your-team/demo" /></Field>
            </div>
          </Section>

          <Section title="Attendance report" sub="The printable report generated per lead." delay="sv-d3">
            <ReportEditor report={form.report_template} setReport={(report_template) => set({ report_template })} />
          </Section>

          <Section title="Thank You screen" sub="Shown after the form is submitted, in this page's branding." delay="sv-d3">
            <ThankYouEditor value={form.thank_you} onChange={(thank_you) => set({ thank_you })} onPreview={() => setTyPreview((v) => !v)} previewing={tyPreview} />
          </Section>

          <Section title="Footer" sub="Optional copyright / privacy line at the bottom of the page. Links (https://…) become clickable." delay="sv-d3">
            <textarea data-testid="campaign-footer-input" rows={3} value={form.footer_text} onChange={(e) => set({ footer_text: e.target.value })}
              className={`${inputCls} h-auto py-3 resize-none text-xs`} placeholder="Copyright © 2026 Your Company. All rights reserved. Privacy policy: https://example.com/privacy" />
          </Section>

          <div className="flex justify-end gap-3">
            <button type="button" onClick={() => nav(-1)} className="h-11 px-6 rounded-full border border-[#242C3D] text-sm hover:bg-white/5">Cancel</button>
            <button data-testid="campaign-save-button" disabled={busy} className="h-11 px-8 rounded-full bg-indigo-500 hover:bg-indigo-400 disabled:opacity-60 text-sm font-medium flex items-center gap-2 transition-colors">
              {busy && <Loader2 className="w-4 h-4 animate-spin" />} {id ? "Save changes" : "Create landing page"}
            </button>
          </div>
        </form>
      </div>

      <aside className="xl:sticky xl:top-24 sv-rise sv-d2" data-testid="landing-preview-panel">
        <div className="flex items-center justify-between mb-3">
          <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500">Live preview</span>
          <span className="font-mono text-[10px] text-slate-600" data-testid="preview-url">/c/{slugPreview || "…"}</span>
        </div>
        <ScaledPreview>
          {tyPreview && form.thank_you.enabled
            ? <ThankYouScreen preview campaign={previewCampaign} />
            : <LandingPage preview campaign={previewCampaign} onSubmit={async () => toast.info("Preview mode — the form is not submitted here")} />}
        </ScaledPreview>
      </aside>
    </div>
  );
}
